import React, { useState, useEffect } from 'react';
import { useEmpresaAuth } from '../../../contexts/EmpresaAuthContext';
import Layout from '../../../components/shared/Layout/Layout';
import Button from '../../../components/shared/Button/Button';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import ImageUploader from '../../../components/shared/ImageUploader/ImageUploader';
import ShareButtons from '../../../components/shared/ShareButtons/ShareButtons';
import { activityShareUrl, activityShareText } from '../../../utils/share';
import { mergeDefaults } from '../../../utils/mergeDefaults';
import {
  ActivitiesContainer,
  Header,
  CreateButton,
  FilterSection,
  ActivitiesList,
  ActivityCard,
  ActivityImage,
  ActivityInfo,
  ActivityTitle,
  ActivityDescription,
  ActivityPrice,
  ActivityStats,
  ActivityActions,
  Modal,
  ModalContent,
  AvailabilitySection,
  TimeSlotGrid,
  TimeSlot,
  CapacitySettings,
  DaySettings,
  EmptyState,
  DayRow,
  DayName,
  TabMenu,
  TabButton
} from './Activities.styles';
import {
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  FormSelect,
  FormGrid,
  FormActions,
  SwitchGroup,
  Switch,
  FormHint
} from '../../../components/shared/Form/Form.styles';

const getDefaultAvailability = () => ({
  capacityType: 'unlimited', // unlimited, daily, time_slots, equipment
  dailyCapacity: 50,
  equipmentCount: 10,
  equipmentType: '',
  timeSlots: {
    enabled: false,
    slots: [
      { time: '09:00', capacity: 10, price: null },
      { time: '11:00', capacity: 10, price: null },
      { time: '13:00', capacity: 10, price: null },
      { time: '15:00', capacity: 10, price: null },
      { time: '17:00', capacity: 10, price: null }
    ]
  },
  operatingDays: {
    monday: { enabled: true, capacity: null },
    tuesday: { enabled: true, capacity: null },
    wednesday: { enabled: true, capacity: null },
    thursday: { enabled: true, capacity: null },
    friday: { enabled: true, capacity: null },
    saturday: { enabled: true, capacity: null },
    sunday: { enabled: true, capacity: null }
  },
  advancedBooking: { minDays: 0, maxDays: 90 },
  seasonalPricing: {
    enabled: false,
    highSeason: { startDate: '', endDate: '', multiplier: 1.2 },
    lowSeason: { startDate: '', endDate: '', multiplier: 0.8 }
  }
});

// Una actividad guardada puede traer la configuración incompleta: se mezcla
// sobre los valores por defecto (ver utils/mergeDefaults).
const mergeAvailability = mergeDefaults;

const Activities = () => {
  const { empresa } = useEmpresaAuth();
  const [activities, setActivities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [activeTab, setActiveTab] = useState('basic'); // basic, availability, pricing
  const [uploadingImages, setUploadingImages] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    longDescription: '',
    price: '',
    duration: {    // ✅ NUEVO FORMATO
    hours: '',
    minutes: ''
  },
    type: '',
    location: '',
    features: '',
    images: [],
    requireCurp: false
  });

  const [availabilitySettings, setAvailabilitySettings] = useState(getDefaultAvailability);

  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      const response = await fetch('/api/empresa/activities', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('empresa-token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setActivities(data.activities);
      }
    } catch (error) {
      console.error('Error loading activities:', error);
      setActivities([]);
    }
  };

  const handleCreate = () => {
    setEditingActivity(null);
    setActiveTab('basic');
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      longDescription: '',
      price: '',
      duration: {    // ✅ NUEVO FORMATO
        hours: '',
        minutes: ''
      },
      type: '',
      location: '',
      features: '',
      images: [],
      requireCurp: false
    });
    setAvailabilitySettings(getDefaultAvailability());
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    setActiveTab('basic');
  // Función para parsear duración existente
  const parseDuration = (durationStr) => {
    if (!durationStr) return { hours: '', minutes: '' };
    
    // Extraer números y palabras
    const hoursMatch = durationStr.match(/(\d+(?:\.\d+)?)\s*h/i) || durationStr.match(/(\d+(?:\.\d+)?)\s*hora/i);
    const minutesMatch = durationStr.match(/(\d+)\s*m/i) || durationStr.match(/(\d+)\s*minuto/i);
    
    let hours = '';
    let minutes = '';
    
    if (hoursMatch) {
      const hourValue = parseFloat(hoursMatch[1]);
      hours = Math.floor(hourValue).toString();
      
      // Si hay decimal en horas, convertir a minutos
      if (hourValue % 1 !== 0) {
        minutes = Math.round((hourValue % 1) * 60).toString();
      }
    }
    
    if (minutesMatch) {
      minutes = minutesMatch[1];
    }
    
    // Si no hay formato reconocible, asumir que son horas
    if (!hoursMatch && !minutesMatch && /^\d+(\.\d+)?$/.test(durationStr)) {
      const value = parseFloat(durationStr);
      hours = Math.floor(value).toString();
      if (value % 1 !== 0) {
        minutes = Math.round((value % 1) * 60).toString();
      }
    }
    
    return { hours, minutes };
  };

  setFormData({
    title: activity.title,
    description: activity.description,
    longDescription: activity.longDescription,
    price: activity.price,
    duration: parseDuration(activity.duration),  // ✅ NUEVO PARSING
    type: activity.type,
    location: activity.location,
    features: activity.features ? activity.features.join(', ') : '',
    images: activity.images || [],
    requireCurp: Boolean(activity.requireCurp)
  });
    setAvailabilitySettings(
      mergeAvailability(
        getDefaultAvailability(),
        activity.availabilitySettings || activity.availability
      )
    );
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploadingImages) return;
    
    // Función para formatear duración
    const formatDuration = (durationObj) => {
      const { hours, minutes } = durationObj;
      const h = parseInt(hours) || 0;
      const m = parseInt(minutes) || 0;
      
      if (h === 0 && m === 0) return '';
      
      let result = '';
      if (h > 0) {
        result += `${h} hora${h !== 1 ? 's' : ''}`;
      }
      if (m > 0) {
        if (result) result += ' ';
        result += `${m} minuto${m !== 1 ? 's' : ''}`;
      }
      
      return result;
    };

    const activityData = {
      ...formData,
      price: parseFloat(formData.price),
      duration: formatDuration(formData.duration),  // ✅ FORMATEAR DURACIÓN
      features: formData.features.split(',').map(f => f.trim()).filter(f => f),
      availability: availabilitySettings
    };
    
    try {
      const url = editingActivity 
        ? `/api/activities/${editingActivity.id}`
        : '/api/activities';
      
      const method = editingActivity ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('empresa-token')}`
        },
        body: JSON.stringify(activityData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        await loadActivities();
        setShowModal(false);
        setEditingActivity(null);
      } else {
        alert(result.error || 'Error al guardar actividad');
      }
    } catch (error) {
      console.error('Error saving activity:', error);
      alert('Error de conexión');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta actividad?')) {
      try {
        const response = await fetch(`/api/activities/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('empresa-token')}`
          }
        });
        
        const result = await response.json();
        
        if (result.success) {
          await loadActivities();
        } else {
          alert(result.error || 'Error al eliminar actividad');
        }
      } catch (error) {
        console.error('Error deleting activity:', error);
        alert('Error de conexión');
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await fetch(`/api/activities/${id}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('empresa-token')}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        await loadActivities();
      } else {
        alert(result.error || 'Error al cambiar estado');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Error de conexión');
    }
  };

  const handleAddTimeSlot = () => {
    setAvailabilitySettings(prev => ({
      ...prev,
      timeSlots: {
        ...prev.timeSlots,
        slots: [...prev.timeSlots.slots, { time: '18:00', capacity: 10, price: null }]
      }
    }));
  };

  const handleRemoveTimeSlot = (index) => {
    setAvailabilitySettings(prev => ({
      ...prev,
      timeSlots: {
        ...prev.timeSlots,
        slots: prev.timeSlots.slots.filter((_, i) => i !== index)
      }
    }));
  };

  const handleTimeSlotChange = (index, field, value) => {
    setAvailabilitySettings(prev => ({
      ...prev,
      timeSlots: {
        ...prev.timeSlots,
        slots: prev.timeSlots.slots.map((slot, i) => 
          i === index ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  const handleOperatingDayChange = (day, field, value) => {
    setAvailabilitySettings(prev => ({
      ...prev,
      operatingDays: {
        ...prev.operatingDays,
        [day]: {
          ...prev.operatingDays[day],
          [field]: value
        }
      }
    }));
  };

  const dayNames = {
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo'
  };

  const filteredActivities = activities.filter(activity => 
    filterType === 'all' || activity.type.toLowerCase().includes(filterType.toLowerCase())
  );

  return (
    <Layout headerType="empresa">
      <ActivitiesContainer>
        <Header>
          <div>
            <h1>Mis actividades</h1>
            <p>Gestiona todas tus actividades turísticas</p>
          </div>
          <CreateButton onClick={handleCreate}>
            + Nueva actividad
          </CreateButton>
        </Header>

        {empresa && !empresa.verified && (
          <div
            style={{
              background: 'var(--warning-subtle)',
              border: '1px solid var(--warning-border)',
              borderRadius: 'var(--radius-md)',
              padding: '14px 18px',
              marginBottom: '20px',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-secondary)',
            }}
          >
            <strong style={{ color: 'var(--warning-dark)' }}>Empresa en revisión: </strong>
            tus actividades se guardan como borrador y podrás publicarlas cuando
            tu empresa sea verificada.
          </div>
        )}

        <FilterSection>
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">Todos los tipos</option>
            <option value="parque">Parque Acuático</option>
            <option value="aventura">Aventura</option>
            <option value="cultural">Cultural</option>
            <option value="tour">Tours</option>
          </select>
        </FilterSection>

        {filteredActivities.length > 0 ? (
          <ActivitiesList>
            {filteredActivities.map(activity => (
              <ActivityCard key={activity.id} status={activity.status}>
                <ActivityImage src={activity.images?.[0] || '/assets/placeholder.svg'} alt={activity.title} />
                <ActivityInfo>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <ActivityTitle>{activity.title}</ActivityTitle>
                    <StatusBadge status={activity.status} />
                  </div>
                  <ActivityDescription>{activity.description}</ActivityDescription>
                  <div style={{ margin: '12px 0' }}>
                    <ActivityPrice>${activity.price?.toLocaleString()}</ActivityPrice>
                    <span style={{ color: 'var(--text-muted)', marginLeft: '12px', fontSize: 'var(--text-sm)' }}>
                      {activity.duration}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', marginBottom: '8px' }}>
                    📍 {activity.location} · {activity.type}
                  </div>
                  <ActivityStats>
                    <span>{activity.totalReservations || 0} reservas</span>
                    <span>${(activity.monthlyRevenue || 0).toLocaleString()} este mes</span>
                  </ActivityStats>
                  {activity.status === 'active' && (
                    <div style={{ marginTop: '12px' }}>
                      <ShareButtons
                        label="Compartir actividad"
                        title={activity.title}
                        text={activityShareText(activity)}
                        url={activityShareUrl(activity.id)}
                      />
                    </div>
                  )}
                </ActivityInfo>
                <ActivityActions>
                  <button onClick={() => handleEdit(activity)}>✏️ Editar</button>
                  <button onClick={() => handleToggleStatus(activity.id)}>
                    {activity.status === 'active' ? '⏸️ Pausar' : '▶️ Publicar'}
                  </button>
                  <button onClick={() => handleDelete(activity.id)} className="delete">
                    🗑️ Eliminar
                  </button>
                </ActivityActions>
              </ActivityCard>
            ))}
          </ActivitiesList>
        ) : (
          <EmptyState>
            <h3>No tienes actividades registradas</h3>
            <p>Crea tu primera actividad para empezar a recibir reservas</p>
            <Button onClick={handleCreate}>Crear Primera Actividad</Button>
          </EmptyState>
        )}

        {showModal && (
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <h2>{editingActivity ? 'Editar Actividad' : 'Nueva Actividad'}</h2>
              
              <TabMenu>
                <TabButton 
                  active={activeTab === 'basic'} 
                  onClick={() => setActiveTab('basic')}
                >
                  📝 Información Básica
                </TabButton>
                <TabButton 
                  active={activeTab === 'availability'} 
                  onClick={() => setActiveTab('availability')}
                >
                  📅 Disponibilidad
                </TabButton>
                <TabButton 
                  active={activeTab === 'pricing'} 
                  onClick={() => setActiveTab('pricing')}
                >
                  💰 Precios
                </TabButton>
              </TabMenu>
              
              <form onSubmit={handleSubmit}>
                {activeTab === 'basic' && (
                  <>
                    <FormGroup>
                      <FormLabel>Título de la actividad *</FormLabel>
                      <FormInput
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>Descripción corta *</FormLabel>
                      <FormTextarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        rows="2"
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>Descripción completa *</FormLabel>
                      <FormTextarea
                        value={formData.longDescription}
                        onChange={(e) => setFormData({...formData, longDescription: e.target.value})}
                        rows="4"
                        required
                      />
                    </FormGroup>

                    <FormGrid $cols={2}>
                      <FormGroup>
                        <FormLabel>Duración de la actividad *</FormLabel>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          <div>
                            <FormInput
                              type="number"
                              value={formData.duration.hours}
                              onChange={(e) => setFormData({
                                ...formData, 
                                duration: {
                                  ...formData.duration,
                                  hours: e.target.value
                                }
                              })}
                              placeholder="Horas"
                              min="0"
                              max="24"
                            />
                            <small style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Horas</small>
                          </div>
                          <div>
                            <FormInput
                              type="number"
                              value={formData.duration.minutes}
                              onChange={(e) => setFormData({
                                ...formData, 
                                duration: {
                                  ...formData.duration,
                                  minutes: e.target.value
                                }
                              })}
                              placeholder="Minutos"
                              min="0"
                              max="59"
                              step="15"
                            />
                            <small style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Minutos</small>
                          </div>
                        </div>
                        <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
                          Ejemplo: 2 horas 30 minutos, o solo 45 minutos
                        </small>
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>Tipo de actividad *</FormLabel>
                        <FormSelect
                          value={formData.type}
                          onChange={(e) => setFormData({...formData, type: e.target.value})}
                          required
                        >
                          <option value="">Selecciona tipo</option>
                          <option value="Parque Acuático">Parque Acuático</option>
                          <option value="Aventura">Aventura</option>
                          <option value="Cultural">Cultural</option>
                          <option value="Tour">Tour</option>
                          <option value="Acuático">Acuático</option>
                        </FormSelect>
                      </FormGroup>
                    </FormGrid>

                    <FormGroup>
                      <FormLabel>Ubicación *</FormLabel>
                      <FormInput
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        placeholder="ej: Cancún, Quintana Roo"
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>Características incluidas</FormLabel>
                      <FormInput
                        type="text"
                        value={formData.features}
                        onChange={(e) => setFormData({...formData, features: e.target.value})}
                        placeholder="Separar con comas: Guía, Comida, Transporte"
                      />
                    </FormGroup>

                    <SwitchGroup>
                      <Switch>
                        <input
                          type="checkbox"
                          checked={formData.requireCurp}
                          onChange={(e) => setFormData({...formData, requireCurp: e.target.checked})}
                          aria-label="Pedir CURP de cada asistente en las ventas en persona"
                        />
                        <span className="slider"></span>
                      </Switch>
                      <div>
                        <strong>Pedir CURP de cada asistente</strong>
                        <p>
                          En las ventas en persona se pedirá el CURP de cada asistente, con aviso de privacidad que el
                          cliente debe aceptar. Se guarda cifrado y no se incluye en las exportaciones salvo que tú lo pidas.
                        </p>
                      </div>
                    </SwitchGroup>

                    <FormGroup>
                      <FormLabel>Fotos de la actividad</FormLabel>
                      <ImageUploader
                        images={formData.images}
                        onChange={(images) => setFormData((prev) => ({ ...prev, images }))}
                        onBusyChange={setUploadingImages}
                      />
                    </FormGroup>
                  </>
                )}

                {activeTab === 'availability' && (
                  <AvailabilitySection>
                    <FormGroup>
                      <FormLabel>Tipo de gestión de capacidad</FormLabel>
                      <FormSelect
                        value={availabilitySettings.capacityType}
                        onChange={(e) => setAvailabilitySettings({
                          ...availabilitySettings,
                          capacityType: e.target.value
                        })}
                      >
                        <option value="unlimited">Capacidad ilimitada</option>
                        <option value="daily">Límite diario</option>
                        <option value="time_slots">Por horarios específicos</option>
                        <option value="equipment">Por equipo disponible (motos, lanchas, etc.)</option>
                      </FormSelect>
                    </FormGroup>

                    {availabilitySettings.capacityType === 'daily' && (
                      <FormGroup>
                        <FormLabel>Capacidad máxima por día</FormLabel>
                        <FormInput
                          type="number"
                          value={availabilitySettings.dailyCapacity}
                          onChange={(e) => setAvailabilitySettings({
                            ...availabilitySettings,
                            dailyCapacity: parseInt(e.target.value)
                          })}
                          min="1"
                        />
                      </FormGroup>
                    )}

                    {availabilitySettings.capacityType === 'equipment' && (
                      <>
                        <FormGroup>
                          <FormLabel>Tipo de equipo</FormLabel>
                          <FormInput
                            type="text"
                            value={availabilitySettings.equipmentType}
                            onChange={(e) => setAvailabilitySettings({
                              ...availabilitySettings,
                              equipmentType: e.target.value
                            })}
                            placeholder="ej: Cuatrimotos, Lanchas, Kayaks"
                          />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel>Cantidad de equipos disponibles</FormLabel>
                          <FormInput
                            type="number"
                            value={availabilitySettings.equipmentCount}
                            onChange={(e) => setAvailabilitySettings({
                              ...availabilitySettings,
                              equipmentCount: parseInt(e.target.value)
                            })}
                            min="1"
                          />
                        </FormGroup>
                      </>
                    )}

                    {availabilitySettings.capacityType === 'time_slots' && (
                      <>
                        <SwitchGroup>
                          <Switch>
                            <input
                              type="checkbox"
                              checked={availabilitySettings.timeSlots.enabled}
                              onChange={(e) => setAvailabilitySettings({
                                ...availabilitySettings,
                                timeSlots: {
                                  ...availabilitySettings.timeSlots,
                                  enabled: e.target.checked
                                }
                              })}
                            />
                            <span className="slider"></span>
                          </Switch>
                          <span>Gestión por horarios específicos</span>
                        </SwitchGroup>

                        {availabilitySettings.timeSlots.enabled && (
                          <TimeSlotGrid>
                            <h4>Horarios disponibles</h4>
                            {availabilitySettings.timeSlots.slots.map((slot, index) => (
                              <TimeSlot key={index}>
                                <FormInput
                                  type="time"
                                  value={slot.time}
                                  onChange={(e) => handleTimeSlotChange(index, 'time', e.target.value)}
                                />
                                <FormInput
                                  type="number"
                                  value={slot.capacity}
                                  onChange={(e) => handleTimeSlotChange(index, 'capacity', parseInt(e.target.value))}
                                  placeholder="Capacidad"
                                  min="1"
                                />
                                <FormInput
                                  type="number"
                                  value={slot.price || ''}
                                  onChange={(e) => handleTimeSlotChange(index, 'price', e.target.value ? parseFloat(e.target.value) : null)}
                                  placeholder="Precio especial (opcional)"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTimeSlot(index)}
                                  style={{
                                    background: 'var(--danger)',
                                    border: 'none',
                                    color: 'white',
                                    padding: '8px 10px',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: 'pointer',
                                  }}
                                >
                                  🗑️
                                </button>
                              </TimeSlot>
                            ))}
                            <Button type="button" onClick={handleAddTimeSlot} variant="secondary">
                              + Agregar Horario
                            </Button>
                          </TimeSlotGrid>
                        )}
                      </>
                    )}

                    <DaySettings>
                      <h4>Días de operación</h4>
                      {Object.entries(availabilitySettings.operatingDays).map(([day, settings]) => (
                        <DayRow key={day}>
                          <DayName>{dayNames[day]}</DayName>
                          <SwitchGroup>
                            <Switch>
                              <input
                                type="checkbox"
                                checked={settings.enabled}
                                onChange={(e) => handleOperatingDayChange(day, 'enabled', e.target.checked)}
                              />
                              <span className="slider"></span>
                            </Switch>
                          </SwitchGroup>
                          {settings.enabled && (
                            <FormInput
                              type="number"
                              value={settings.capacity || ''}
                              onChange={(e) => handleOperatingDayChange(day, 'capacity', e.target.value ? parseInt(e.target.value) : null)}
                              placeholder="Capacidad especial (opcional)"
                            />
                          )}
                        </DayRow>
                      ))}
                    </DaySettings>

                    <CapacitySettings>
                      <h4>Configuración de reservas anticipadas</h4>
                      <FormGrid $cols={2}>
                        <FormGroup>
                          <FormLabel>Mínimo días de anticipación</FormLabel>
                          <FormInput
                            type="number"
                            value={availabilitySettings.advancedBooking.minDays}
                            onChange={(e) => setAvailabilitySettings({
                              ...availabilitySettings,
                              advancedBooking: {
                                ...availabilitySettings.advancedBooking,
                                minDays: parseInt(e.target.value)
                              }
                            })}
                            min="0"
                          />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel>Máximo días de anticipación</FormLabel>
                          <FormInput
                            type="number"
                            value={availabilitySettings.advancedBooking.maxDays}
                            onChange={(e) => setAvailabilitySettings({
                              ...availabilitySettings,
                              advancedBooking: {
                                ...availabilitySettings.advancedBooking,
                                maxDays: parseInt(e.target.value)
                              }
                            })}
                            min="1"
                          />
                        </FormGroup>
                      </FormGrid>
                    </CapacitySettings>
                  </AvailabilitySection>
                )}

                {activeTab === 'pricing' && (
                  <>
                    <FormGroup>
                      <FormLabel>Precio base (MXN, IVA incluido) *</FormLabel>
                      <FormInput
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        required
                        min="0"
                        step="0.01"
                      />
                      <FormHint>
                        Es el precio final al público, con el IVA (16 %) ya incluido: lo que el cliente ve es lo que paga.
                      </FormHint>
                    </FormGroup>

                    <SwitchGroup>
                      <Switch>
                        <input
                          type="checkbox"
                          checked={availabilitySettings.seasonalPricing.enabled}
                          onChange={(e) => setAvailabilitySettings({
                            ...availabilitySettings,
                            seasonalPricing: {
                              ...availabilitySettings.seasonalPricing,
                              enabled: e.target.checked
                            }
                          })}
                        />
                        <span className="slider"></span>
                      </Switch>
                      <div>
                        <strong>Precios estacionales</strong>
                        <p>Configura precios diferentes para temporada alta y baja</p>
                      </div>
                    </SwitchGroup>

                    {availabilitySettings.seasonalPricing.enabled && (
                      <>
                        <h4>Temporada Alta</h4>
                        <FormGrid $cols={3}>
                          <FormGroup>
                            <FormLabel>Fecha inicio</FormLabel>
                            <FormInput
                              type="date"
                              value={availabilitySettings.seasonalPricing.highSeason.startDate}
                              onChange={(e) => setAvailabilitySettings({
                                ...availabilitySettings,
                                seasonalPricing: {
                                  ...availabilitySettings.seasonalPricing,
                                  highSeason: {
                                    ...availabilitySettings.seasonalPricing.highSeason,
                                    startDate: e.target.value
                                  }
                                }
                              })}
                            />
                          </FormGroup>
                          <FormGroup>
                            <FormLabel>Fecha fin</FormLabel>
                            <FormInput
                              type="date"
                              value={availabilitySettings.seasonalPricing.highSeason.endDate}
                              onChange={(e) => setAvailabilitySettings({
                                ...availabilitySettings,
                                seasonalPricing: {
                                  ...availabilitySettings.seasonalPricing,
                                  highSeason: {
                                    ...availabilitySettings.seasonalPricing.highSeason,
                                    endDate: e.target.value
                                  }
                                }
                              })}
                            />
                          </FormGroup>
                          <FormGroup>
                            <FormLabel>Multiplicador de precio</FormLabel>
                            <FormInput
                              type="number"
                              value={availabilitySettings.seasonalPricing.highSeason.multiplier}
                              onChange={(e) => setAvailabilitySettings({
                                ...availabilitySettings,
                                seasonalPricing: {
                                  ...availabilitySettings.seasonalPricing,
                                  highSeason: {
                                    ...availabilitySettings.seasonalPricing.highSeason,
                                    multiplier: parseFloat(e.target.value)
                                  }
                                }
                              })}
                              step="0.1"
                              min="0.1"
                            />
                          </FormGroup>
                        </FormGrid>

                        <h4>Temporada Baja</h4>
                        <FormGrid $cols={3}>
                          <FormGroup>
                            <FormLabel>Fecha inicio</FormLabel>
                            <FormInput
                              type="date"
                              value={availabilitySettings.seasonalPricing.lowSeason.startDate}
                              onChange={(e) => setAvailabilitySettings({
                                ...availabilitySettings,
                                seasonalPricing: {
                                  ...availabilitySettings.seasonalPricing,
                                  lowSeason: {
                                    ...availabilitySettings.seasonalPricing.lowSeason,
                                    startDate: e.target.value
                                  }
                                }
                              })}
                            />
                          </FormGroup>
                          <FormGroup>
                            <FormLabel>Fecha fin</FormLabel>
                            <FormInput
                              type="date"
                              value={availabilitySettings.seasonalPricing.lowSeason.endDate}
                              onChange={(e) => setAvailabilitySettings({
                                ...availabilitySettings,
                                seasonalPricing: {
                                  ...availabilitySettings.seasonalPricing,
                                  lowSeason: {
                                    ...availabilitySettings.seasonalPricing.lowSeason,
                                    endDate: e.target.value
                                  }
                                }
                              })}
                            />
                          </FormGroup>
                          <FormGroup>
                            <FormLabel>Multiplicador de precio</FormLabel>
                            <FormInput
                              type="number"
                              value={availabilitySettings.seasonalPricing.lowSeason.multiplier}
                              onChange={(e) => setAvailabilitySettings({
                                ...availabilitySettings,
                                seasonalPricing: {
                                  ...availabilitySettings.seasonalPricing,
                                  lowSeason: {
                                    ...availabilitySettings.seasonalPricing.lowSeason,
                                    multiplier: parseFloat(e.target.value)
                                  }
                                }
                              })}
                              step="0.1"
                              min="0.1"
                            />
                          </FormGroup>
                        </FormGrid>
                      </>
                    )}
                  </>
                )}

                <FormActions>
                  <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                    Cancelar
                  </Button>
                  {activeTab !== 'pricing' ? (
                    // key distinto: sin él React reutiliza el mismo <button> y, al pasar a la
                    // última pestaña, el clic en "Siguiente" se convierte en submit y guarda
                    // la actividad antes de mostrar Precios.
                    <Button key="next" type="button" onClick={() => {
                      if (activeTab === 'basic') setActiveTab('availability');
                      else if (activeTab === 'availability') setActiveTab('pricing');
                    }}>
                      Siguiente →
                    </Button>
                  ) : (
                    <Button key="submit" type="submit" disabled={uploadingImages}>
                      {editingActivity ? 'Actualizar' : 'Crear'} Actividad
                    </Button>
                  )}
                </FormActions>
              </form>
            </ModalContent>
          </Modal>
        )}
      </ActivitiesContainer>
    </Layout>
  );
};

export default Activities;
